import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Button,
  Box,
  IconButton,
  useTheme
} from '@mui/material';
import {
  Add,
  Person,
  CreditCard,
  Assessment,
  AccountBalance,
  Receipt,
  Notifications,
  Settings,
  MoreVert
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const QuickActionButton = ({ icon: Icon, title, description, color, onClick, delay = 0 }) => {
  const theme = useTheme();
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, delay }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <Button
        variant="outlined"
        onClick={onClick}
        sx={{
          p: 2,
          height: '100%',
          minHeight: 100,
          borderRadius: 3,
          border: (theme) => theme.palette.mode === 'dark'
            ? `2px solid ${theme.palette[color].main}20`
            : `2px solid ${theme.palette[color].main}15`,
          backgroundColor: (theme) => theme.palette.mode === 'dark'
            ? `${theme.palette[color].main}05`
            : `${theme.palette[color].main}05`,
          '&:hover': {
            backgroundColor: (theme) => theme.palette.mode === 'dark'
              ? `${theme.palette[color].main}15`
              : `${theme.palette[color].main}10`,
            borderColor: (theme) => theme.palette.mode === 'dark'
              ? `${theme.palette[color].main}40`
              : `${theme.palette[color].main}30`,
            transform: 'translateY(-2px)',
            boxShadow: (theme) => theme.palette.mode === 'dark'
              ? theme.shadows[8]
              : theme.shadows[4],
          },
          transition: 'all 0.3s ease-in-out',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          textTransform: 'none',
        }}
        fullWidth
      >
        <Box
          sx={{
            p: 1,
            borderRadius: 2,
            backgroundColor: (theme) => theme.palette.mode === 'dark'
              ? `${theme.palette[color].main}20`
              : `${theme.palette[color].main}15`,
            color: `${theme.palette[color].main}`,
            mb: 1,
          }}
        >
          <Icon fontSize="medium" />
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
          {description}
        </Typography>
      </Button>
    </motion.div>
  );
};

const QuickActions = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const quickActions = [
    {
      icon: Person,
      title: 'Add Member',
      description: 'Register new member',
      color: 'primary',
      onClick: () => console.log('Add Member'),
    },
    {
      icon: CreditCard,
      title: 'New Loan',
      description: 'Process loan application',
      color: 'success',
      onClick: () => navigate('/loan-types'),
    },
    {
      icon: Receipt,
      title: 'Record Payment',
      description: 'Log member payment',
      color: 'info',
      onClick: () => console.log('Record Payment'),
    },
    {
      icon: Assessment,
      title: 'Generate Report',
      description: 'Create financial report',
      color: 'warning',
      onClick: () => console.log('Generate Report'),
    },
    {
      icon: AccountBalance,
      title: 'Account Master',
      description: 'Manage accounts',
      color: 'secondary',
      onClick: () => navigate('/account-master'),
    },
    {
      icon: Notifications,
      title: 'Send Reminders',
      description: 'Payment notifications',
      color: 'error',
      onClick: () => console.log('Send Reminders'),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      <Card
        sx={{
          borderRadius: 3,
          backgroundColor: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.04)'
            : 'rgba(0, 0, 0, 0.02)',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: (theme) => theme.palette.mode === 'dark'
            ? '0px 4px 12px rgba(0, 0, 0, 0.3)'
            : '0px 2px 8px rgba(0, 0, 0, 0.08)',
        }}
      >
        <CardHeader
          title={
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
              Quick Actions
            </Typography>
          }
          action={
            <IconButton
              size="small"
              sx={{
                color: 'text.secondary',
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.04)',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.12)'
                    : 'rgba(0, 0, 0, 0.08)'
                }
              }}
            >
              <MoreVert />
            </IconButton>
          }
          sx={{ pb: 1 }}
        />
        <CardContent>
          <Grid container spacing={2}>
            {quickActions.map((action, index) => (
              <Grid item xs={6} sm={4} md={2} key={index}>
                <QuickActionButton {...action} delay={index * 0.1} />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default QuickActions;